// Account-list URL ↔ filter serialisation — FE-038.
//
// Dashboard drill-through links from FE-031..FE-037 produce
// ``/dashboard/accounts?...`` URLs whose query keys we already
// pinned through tests in ``drillThrough.test.ts``. This helper is
// the inverse: parse those keys back into the ``AccountListFilters``
// shape, and re-serialise them when the user toggles a chip so the
// URL stays the source of truth (deep-linkable, shareable, back-
// button friendly).

import type {
  AccountListFilters,
  AccountListSortKey,
} from "./types";


export const DEFAULT_LIMIT = 20;
export const DEFAULT_SORT_BY: AccountListSortKey = "overdue_commitments";
export const DEFAULT_SORT_DIR: "asc" | "desc" = "desc";


function readBool(value: string | null): boolean | undefined {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}


function readEnum<T extends string>(
  value: string | null,
  allowed: readonly T[],
): T | undefined {
  if (value === null) return undefined;
  return (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}


export function parseFiltersFromSearch(
  search: URLSearchParams,
): AccountListFilters {
  return {
    search: search.get("search") ?? undefined,
    status: readEnum(search.get("status"), ["active", "archived", "all"] as const),
    risk_level: readEnum(
      search.get("risk_level"),
      ["low", "medium", "high", "critical"] as const,
    ),
    has_overdue: readBool(search.get("has_overdue")),
    going_cold: readBool(search.get("going_cold")),
    renewal_month: search.get("renewal_month") ?? undefined,
    renewal_status: readEnum(
      search.get("renewal_status"),
      ["on_track", "behind", "at_risk", "not_started"] as const,
    ),
    renewal_attention: readBool(search.get("renewal_attention")),
    id: search.get("id") ?? undefined,
  };
}


export function parseSortFromSearch(
  search: URLSearchParams,
): { sortBy: AccountListSortKey; sortDir: "asc" | "desc" } {
  const allowedSort: AccountListSortKey[] = [
    "legal_name", "open_commitments", "overdue_commitments",
    "renewal_date", "updated_at",
  ];
  const sortByRaw = search.get("sort_by");
  const sortBy = allowedSort.includes(
    sortByRaw as AccountListSortKey,
  ) ? (sortByRaw as AccountListSortKey) : DEFAULT_SORT_BY;
  const sortDirRaw = (search.get("sort_dir") ?? "").toLowerCase();
  const sortDir =
    sortDirRaw === "asc" || sortDirRaw === "desc"
      ? (sortDirRaw as "asc" | "desc")
      : DEFAULT_SORT_DIR;
  return { sortBy, sortDir };
}


export function parseOffsetFromSearch(
  search: URLSearchParams,
): number {
  const raw = Number(search.get("offset") ?? "0");
  return Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 0;
}


export function serializeAccountListUrl(
  filters: AccountListFilters,
  sortBy: AccountListSortKey,
  sortDir: "asc" | "desc",
  offset: number,
): string {
  const sp = new URLSearchParams();
  appendString(sp, "search", filters.search);
  appendString(sp, "status", filters.status);
  appendString(sp, "risk_level", filters.risk_level);
  appendBool(sp, "has_overdue", filters.has_overdue);
  appendBool(sp, "going_cold", filters.going_cold);
  appendString(sp, "renewal_month", filters.renewal_month);
  appendString(sp, "renewal_status", filters.renewal_status);
  appendBool(sp, "renewal_attention", filters.renewal_attention);
  appendString(sp, "id", filters.id);
  if (sortBy !== DEFAULT_SORT_BY) sp.append("sort_by", sortBy);
  if (sortDir !== DEFAULT_SORT_DIR) sp.append("sort_dir", sortDir);
  if (offset > 0) sp.append("offset", String(offset));
  // Sort keys for deterministic output (snapshot-friendly tests +
  // stable shareable URLs).
  const entries = Array.from(sp.entries()).sort(
    ([a], [b]) => (a < b ? -1 : a > b ? 1 : 0),
  );
  if (entries.length === 0) return "/dashboard/accounts";
  const sorted = new URLSearchParams();
  for (const [k, v] of entries) sorted.append(k, v);
  return `/dashboard/accounts?${sorted.toString()}`;
}


function appendString(
  sp: URLSearchParams, key: string, value: string | undefined,
): void {
  if (value === undefined || value === null || value === "") return;
  sp.append(key, value);
}


function appendBool(
  sp: URLSearchParams, key: string, value: boolean | undefined,
): void {
  if (value === undefined) return;
  sp.append(key, value ? "true" : "false");
}
